import { RiSearchLine } from 'react-icons/ri';
import { useEffect, useState, type ChangeEvent, type JSX } from 'react';
import {
  OFFER_TYPE_LABELS,
  OFFER_TYPES,
  ORDERING_OPTIONS,
} from '../BrandOffersPage.constants';
import type { OfferFiltersPanelProps } from '../BrandOffersPage.types';
import { OFFER_FILTERS_PANEL } from './OfferFiltersPanel.constants';
import styles from './OfferFiltersPanel.module.css';

const OfferFiltersPanel = ({
  filters,
  brands,
  onChange,
}: OfferFiltersPanelProps): JSX.Element => {
  const [brandInput, setBrandInput] = useState('');

  useEffect(() => {
    if (!filters.brand) {
      setBrandInput('');
      return;
    }
    const b = brands.find((x) => String(x.id) === filters.brand);
    if (b) setBrandInput(b.name);
  }, [filters.brand, brands]);

  const handleBrandInput = (e: ChangeEvent<HTMLInputElement>): void => {
    const text = e.target.value;
    setBrandInput(text);
    const match = brands.find(
      (b) => b.name.toLowerCase() === text.toLowerCase()
    );
    onChange({ brand: match ? String(match.id) : '' });
  };

  return (
    <aside
      className={styles.sidebar}
      aria-label={OFFER_FILTERS_PANEL.asideAria}
    >
      <p className={styles.sidebarHeading}>{OFFER_FILTERS_PANEL.heading}</p>

      <div className={styles.filterBlock}>
        <label
          className={styles.filterLabel}
          htmlFor={OFFER_FILTERS_PANEL.brandInputId}
        >
          {OFFER_FILTERS_PANEL.brandLabel}
        </label>
        <div className={styles.searchWrap}>
          <RiSearchLine className={styles.searchIcon} size={16} />
          <input
            id={OFFER_FILTERS_PANEL.brandInputId}
            className={styles.searchInput}
            type="search"
            placeholder={OFFER_FILTERS_PANEL.brandPlaceholder}
            list={OFFER_FILTERS_PANEL.brandListId}
            value={brandInput}
            onChange={handleBrandInput}
          />
          <datalist id={OFFER_FILTERS_PANEL.brandListId}>
            {brands.map((b) => (
              <option key={b.id} value={b.name} />
            ))}
          </datalist>
        </div>
      </div>

      <div className={styles.filterBlock}>
        <label
          className={styles.filterLabel}
          htmlFor={OFFER_FILTERS_PANEL.typeSelectId}
        >
          {OFFER_FILTERS_PANEL.typeLabel}
        </label>
        <select
          id={OFFER_FILTERS_PANEL.typeSelectId}
          className={styles.filterSelect}
          value={filters.offerType}
          onChange={(e) => onChange({ offerType: e.target.value })}
        >
          <option value="">{OFFER_FILTERS_PANEL.typeAllOption}</option>
          {OFFER_TYPES.map((t) => (
            <option key={t} value={t}>
              {OFFER_TYPE_LABELS[t]}
            </option>
          ))}
        </select>
      </div>

      <div className={styles.filterBlock}>
        <label
          className={styles.filterLabel}
          htmlFor={OFFER_FILTERS_PANEL.liveSelectId}
        >
          {OFFER_FILTERS_PANEL.liveLabel}
        </label>
        <select
          id={OFFER_FILTERS_PANEL.liveSelectId}
          className={styles.filterSelect}
          value={filters.isActive}
          onChange={(e) => onChange({ isActive: e.target.value })}
        >
          <option value="">{OFFER_FILTERS_PANEL.liveBoth}</option>
          <option value="true">{OFFER_FILTERS_PANEL.liveOnly}</option>
          <option value="false">{OFFER_FILTERS_PANEL.pausedOnly}</option>
        </select>
      </div>

      <div className={styles.filterBlock}>
        <label
          className={styles.filterLabel}
          htmlFor={OFFER_FILTERS_PANEL.orderSelectId}
        >
          {OFFER_FILTERS_PANEL.orderLabel}
        </label>
        <select
          id={OFFER_FILTERS_PANEL.orderSelectId}
          className={styles.filterSelect}
          value={filters.ordering}
          onChange={(e) => onChange({ ordering: e.target.value })}
        >
          {ORDERING_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
    </aside>
  );
};

export default OfferFiltersPanel;
